/**
 * 无分组的一维数组排序
 * - sortByPrimaryValue: 通过配置主键和取值方式，对数组进行基础排序
 */

import { IFlatSortConfig } from './types';
import { convertToSortableValue } from './valueUtils';

/**
 * 无分组的基础排序方法
 * 根据 primaryKey 定位需要参与排序的数据，默认按数值降序排序。
 */
export function sortByPrimaryValue<T>(
  items: T[],
  config: IFlatSortConfig<T>,
): T[] {
  if (!Array.isArray(items)) {
    console.error(
      'sortByPrimaryValue expects an array, received:',
      typeof items,
    );
    return [];
  }
  if (items.length <= 1) return items;

  const {
    primaryKey,
    keyExtractor,
    valueExtractor,
    numericConverter,
    compareFn,
  } = config;

  if (!primaryKey) return items;

  const resolvePrimaryValue = (item: T): any =>
    typeof primaryKey === 'string' ? primaryKey : primaryKey(item);
  // 支持字符串主键或函数主键，统一拿到主键值

  const getItemKey = (item: T, resolvedPrimaryValue?: any): any => {
    if (keyExtractor) {
      return keyExtractor(item);
    }
    if (typeof primaryKey === 'string') {
      return (item as any)[primaryKey];
    }
    if (resolvedPrimaryValue !== undefined) {
      return resolvedPrimaryValue;
    }
    return primaryKey(item);
  };

  const getValue = (item: T, resolvedPrimaryValue?: any): any => {
    if (valueExtractor) {
      return valueExtractor(item);
    }
    return getItemKey(item, resolvedPrimaryValue);
  };

  const convertValue =
    numericConverter ||
    ((value: any) => convertToSortableValue(value));
  // 统一把值转换成可比较的数值，外部可自定义转换

  const decorated = items.map((item, index) => {
    const resolvedPrimaryValue = resolvePrimaryValue(item);
    const itemKey = getItemKey(item, resolvedPrimaryValue);
    const matches = itemKey === resolvedPrimaryValue;

    if (!matches) {
      // 主键不匹配时打标并置为最小值，避免脏数据干扰排序
      return {
        item,
        numeric: Number.NEGATIVE_INFINITY,
        hasValidValue: false,
        order: index,
      };
    }

    const value = getValue(item, resolvedPrimaryValue);
    const { numeric, isValid } = convertValue(value);

    return {
      item,
      numeric,
      hasValidValue: isValid,
      order: index,
    };
  });

  const defaultCompare = (
    a: (typeof decorated)[number],
    b: (typeof decorated)[number],
  ): number => {
    if (a.numeric === b.numeric) {
      // 保持稳定排序：数值相等时按原始顺序
      return a.order - b.order;
    }
    return b.numeric - a.numeric;
  };

  const sorted = decorated.sort(compareFn || defaultCompare);

  return sorted.map(({ item }) => item);
}


